import { useState, useEffect, useCallback, useMemo } from "react";
import { notification, Button, Space } from "antd";
import { FormOutlined, PlusOutlined, ApiOutlined } from "@ant-design/icons";
import { hasPrivilege } from "../../api/token.js";
import service from "../../services/Service.js";
import { withLocale } from "../../l10n/index.jsx";
import DataTable from "../DataTable/index.js";
import DeleteButton from "../DeleteButton/DeleteButton.jsx";
import withNavigation from '../hooks/withNavigation.jsx';
import { getModemBaseColumns } from '../../utils/modemCommonColumns.jsx';
import { compareDates, formatDate } from '../../utils/dates';
import { useGetModemTypesLookupQuery } from '../../services/lookupApi.js';
import ModemImportModal from '../ModemImportModal';


const EMPTY_ARRAY = [];

function ModemManufactureList({ lang, navigate }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    loading: false,
    importModalVisible: false,
  });

  const [data, setData] = useState({
    raw: [],
    selected: null,
  });

  // ===== 2. SELECTORES / CONSTANTES =====
  const { data: modemTypes = EMPTY_ARRAY } = useGetModemTypesLookupQuery();

  // ===== 3. DERIVADOS =====
  const selectedUuid = data.selected?.uuid || "";

  const typeFilter = useMemo(
    () => modemTypes.map(type => ({ text: type.code, value: type.uuid })),
    [modemTypes]
  );

  const selectedDeviceTypes = useMemo(() => {
    if (!data.selected) return EMPTY_ARRAY;
    const type = modemTypes.find(item => item.uuid === data.selected.modem_type_uuid);
    return [...new Set((type?.models ?? []).map(model => model.device_type))];
  }, [data.selected, modemTypes]);

  const allowsRouter = selectedDeviceTypes.includes('router');
  const allowsBox = selectedDeviceTypes.includes('kibox');

  // ===== 4. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const Pfetch = useCallback(async () => {
    try {
      const res = await service.get("modems", { free: true });

      if (!res || res.data.ok === false) {
        notification["error"]({
          message: lang.handle(res?.data?.message || "Error al obtener datos"),
        });
        updateData('raw', []);
        return;
      }

      updateData('raw', res.data.data || []);
    } catch (err) {
      console.error("Error devuelto: " + err);
    }
  }, [lang]);


  // ===== 6. HANDLERS =====
  const deleteModem = async () => {
    updateUI('loading', true);
    try {
      const res = await service.del(`modems/${selectedUuid}`);
      if (!res) return;

      if (res.data.ok === false) {
        notification["error"]({ message: lang.handle(res.data.message) });
      } else {
        notification["success"]({ message: lang.handle('Modem borrado correctamente') });
      }
      await Pfetch();
      updateData('selected', null);
    } catch (err) {
      console.error("Error devuelto: " + err);
    } finally {
      updateUI('loading', false);
    }
  };

  const editModem = () => navigate(`/modems/edit/${selectedUuid}`);
  const newModem = () => navigate('/modems/new');
  const createRouter = () => navigate(`/boards/manufacture/new/3?modem_imei=${data.selected.imei}`);
  const createBox = () => navigate(`/boards/manufacture/new/1?modem_imei=${data.selected.imei}`);

  // ===== 7. EFFECTS =====
  useEffect(() => {
    updateUI('loading', true);
    Pfetch().then(() => updateUI('loading', false));
  }, [Pfetch]);

  // ===== 8. CONFIG =====
  const onSelectionChange = (selectedRowKeys, selectedRows) => {
    updateData('selected', selectedRows.length > 0 ? selectedRows[0] : null);
  };

  const columns = [
    ...getModemBaseColumns(lang, typeFilter),
    {
      title: lang.handle('Fecha alta'),
      dataIndex: "create_ts",
      render: (value) => formatDate(value) || '-',
      sorter: (a, b) => compareDates(a.create_ts, b.create_ts),
      sortDirections: ["descend", "ascend"],
    },
  ]

  let selection = null;
  if (hasPrivilege('modem_edit')) {
    selection = {
      type: 'radio',
      selectedRowKeys: data.selected?.uuid ? [data.selected.uuid] : [],
      onChange: onSelectionChange,
    };
  }

  // ===== 9. RENDER =====
  return (
    <div>
      <DataTable
        rowSelection={selection}
        columns={columns}
        rowKey="uuid"
        dataSource={data.raw}
        loading={ui.loading}
      />
      <div className="button-panel">
        <Space>
          {/* BTN NUEVO MODEM */}
          {hasPrivilege('modem_edit') && (
            <Button type="primary" icon={<PlusOutlined />} onClick={newModem}>
              {lang.handle('Nuevo modem')}
            </Button>
          )}
          {/* BTN IMPORTAR MODEM */}
          {hasPrivilege('modem_edit') && (
            <Button type="primary" onClick={() => updateUI('importModalVisible', true)}>
              {lang.handle('Importar')}
            </Button>
          )}

          {/* BTN EDITAR MODEM */}
          {hasPrivilege('modem_edit') && (
            <Button
              type="primary"
              icon={<FormOutlined />}
              disabled={!data.selected}
              onClick={editModem}
            >
              {lang.handle('Editar')}
            </Button>
          )}
          {/* BTN CREAR ROUTER */}
          {hasPrivilege('manufacture_create') && (
            <Button
              type="primary"
              icon={<ApiOutlined />}
              disabled={!data.selected || !allowsRouter}
              onClick={createRouter}
            >
              {lang.handle('Crear router')}
            </Button>
          )}
          {/* BTN CREAR BOX */}
          {hasPrivilege('manufacture_create') && (
            <Button
              type="primary"
              icon={<ApiOutlined />}
              disabled={!data.selected || !allowsBox}
              onClick={createBox}
            >
              {lang.handle('Crear box')}
            </Button>
          )}
          {/* BTN ELIMINAR MODEM */}
          {hasPrivilege('modem_edit') && (
            <DeleteButton onEvent={deleteModem} buttonDisabled={!data.selected} />
          )}
        </Space>
      </div>
        <ModemImportModal
          open={ui.importModalVisible}
          onClose={() => updateUI('importModalVisible', false)}
          onImported={Pfetch}
        />
    </div>
  );
}

export default withLocale(withNavigation(ModemManufactureList));
